import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, todayIso } from "@/lib/entries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import EntryDialog from "@/components/EntryDialog";
import BulkEntryDialog from "@/components/BulkEntryDialog";
import StatusBadge from "@/components/StatusBadge";
import PatientHistoryDialog from "@/components/PatientHistoryDialog";
import { Plus, Users, Search, X, Sparkles, Trash2, ClipboardList, CheckCircle2, Circle, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const VISIBLE_STRIP = 5;

function isoLocal(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shiftIso(iso, days) {
  const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + days); return isoLocal(d);
}

function shortLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dayZero = new Date(d); dayZero.setHours(0, 0, 0, 0);
  const diff = Math.round((today - dayZero) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short" });
}

function dayNumber(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function DailyLog() {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [summaryDate, setSummaryDate] = useState(todayIso());
  const [anchorDate, setAnchorDate] = useState(todayIso()); // rightmost visible date
  const [allRecords, setAllRecords] = useState(true); // when true, list shows every entry regardless of date
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [historyName, setHistoryName] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [visibleDays, setVisibleDays] = useState(15); // 15-day rolling window when on All records

  const load = useCallback(async () => {
    try {
      const params = {};
      if (!allRecords && summaryDate) params.date = summaryDate;
      if (q) params.q = q;
      const [{ data: filtered }, { data: all }] = await Promise.all([
        api.get("/entries", { params }),
        api.get("/entries"),
      ]);
      setRows(filtered);
      setAllRows(all);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [allRecords, summaryDate, q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const deleteEntry = async (e) => {
    if (deletingId) return;
    if (!window.confirm(`Delete "${e.patient_name}"? This can't be undone.`)) return;
    setDeletingId(e.id);
    try {
      await api.delete(`/entries/${e.id}`);
      setRows((prev) => prev.filter((r) => r.id !== e.id));
      toast.success("Entry deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  // Newest date first; created_at as tiebreaker so same-day rows stay stable.
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (a.entry_date !== b.entry_date) return a.entry_date < b.entry_date ? 1 : -1;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }, [rows]);

  // When on "All records", limit to a rolling window that starts at today and
  // extends `visibleDays` days back. "Load more" widens the window by 15 more
  // days. On single-date and searched views we don't apply the window.
  const windowStartIso = useMemo(() => shiftIso(todayIso(), -(visibleDays - 1)), [visibleDays]);
  const windowed = useMemo(() => {
    if (!allRecords) return sorted;
    return sorted.filter((r) => r.entry_date >= windowStartIso);
  }, [sorted, allRecords, windowStartIso]);
  const hasMoreOlder = useMemo(() => {
    if (!allRecords) return false;
    return sorted.some((r) => r.entry_date < windowStartIso);
  }, [sorted, allRecords, windowStartIso]);
  const loadMoreDays = () => setVisibleDays((v) => v + 15);

  const visibleStrip = useMemo(() => {
    // Rendered left-to-right: [anchor-4, anchor-3, anchor-2, anchor-1, anchor]
    const out = [];
    for (let i = VISIBLE_STRIP - 1; i >= 0; i--) out.push(shiftIso(anchorDate, -i));
    return out;
  }, [anchorDate]);

  const summaryByDate = useMemo(() => {
    const map = {};
    for (const d of visibleStrip) map[d] = { total: 0, completed: 0, incomplete: 0 };
    if (!map[summaryDate]) map[summaryDate] = { total: 0, completed: 0, incomplete: 0 };
    for (const r of allRows) {
      if (!map[r.entry_date]) continue;
      map[r.entry_date].total += 1;
      if (r.is_new) map[r.entry_date].incomplete += 1;
      else map[r.entry_date].completed += 1;
    }
    return map;
  }, [allRows, visibleStrip, summaryDate]);

  const active = summaryByDate[summaryDate] || { total: 0, completed: 0, incomplete: 0 };
  const today = todayIso();
  const canGoNewer = summaryDate < today;

  const shiftLeft = () => {
    const newDate = shiftIso(summaryDate, -1);
    setAnchorDate(newDate);
    setSummaryDate(newDate);
    setAllRecords(false);
  };
  const shiftRight = () => {
    if (summaryDate >= today) return;
    const newDate = shiftIso(summaryDate, 1);
    setAnchorDate(newDate);
    setSummaryDate(newDate);
    setAllRecords(false);
  };

  const pickDate = (d) => {
    setSummaryDate(d);
    setAllRecords(false);
  };
  const pickAll = () => {
    setAllRecords(true);
    // Also snap the 5-day window back to today (today selected as the rightmost pill)
    setSummaryDate(today);
    setAnchorDate(today);
    setVisibleDays(15); // reset rolling window to default 15 days
  };
  const pickFromCalendar = (iso) => {
    if (!iso) return;
    // Cap at today (no future dates)
    const capped = iso > today ? today : iso;
    setSummaryDate(capped);
    setAnchorDate(capped);   // selected date lands at the rightmost of the 5-day window
    setAllRecords(false);
  };

  return (
    <div className="px-6 lg:px-10 pt-6 lg:pt-10 pb-0 max-w-[1400px] mx-auto">
      {/* Frozen top panel: title + date strip (5 at a time) + actions all on one row */}
      <div className="sticky top-0 z-20 -mx-6 lg:-mx-10 -mt-6 lg:-mt-10 px-6 lg:px-10 py-3 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Main entry log</p>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 leading-tight">Daily Log</h1>
          </div>

          <div className="flex items-center gap-2" data-testid="daily-date-strip">
            <button
              type="button"
              onClick={pickAll}
              data-testid="strip-all-button"
              className={`h-9 px-3 rounded border text-xs font-semibold transition-colors ${
                allRecords
                  ? "border-sky-500 bg-sky-500 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50"
              }`}
              title="Show every entry in the log"
            >
              All records
            </button>
            <button
              type="button"
              onClick={shiftLeft}
              aria-label="Older days"
              data-testid="strip-prev-button"
              className="h-9 w-7 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="flex gap-1.5">
              {visibleStrip.map((d) => {
                const isActive = d === summaryDate;
                const s = summaryByDate[d] || { total: 0, completed: 0, incomplete: 0 };
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => pickDate(d)}
                    data-testid={`daily-strip-${d}`}
                    className={`shrink-0 rounded-md border px-2 py-1 text-center transition-colors min-w-[60px] ${
                      isActive
                        ? "border-sky-500 bg-sky-500 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50"
                    }`}
                  >
                    <div className={`text-[9px] font-semibold uppercase tracking-wide leading-none ${isActive ? "text-sky-50" : "text-slate-400"}`}>
                      {shortLabel(d)}
                    </div>
                    <div className={`text-xs font-semibold leading-tight mt-0.5 ${isActive ? "text-white" : "text-slate-800"}`}>
                      {dayNumber(d)}
                    </div>
                    <div className={`text-[9px] leading-none ${isActive ? "text-sky-50" : "text-slate-500"}`}>
                      {s.total}
                    </div>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              disabled={!canGoNewer}
              onClick={shiftRight}
              aria-label="Newer days"
              data-testid="strip-next-button"
              className="h-9 w-7 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight size={14} />
            </button>
            <label
              className="relative h-9 rounded border border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-sky-300 flex items-center gap-1 px-2 cursor-pointer"
              title="Pick any date from the calendar"
              data-testid="strip-calendar-label"
            >
              <CalendarDays size={14} />
              <input
                type="date"
                max={today}
                value={allRecords ? "" : summaryDate}
                onChange={(e) => pickFromCalendar(e.target.value)}
                data-testid="strip-calendar-input"
                className="absolute inset-0 opacity-0 cursor-pointer"
                aria-label="Pick a date"
              />
            </label>
            <div className="relative w-56">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-8 h-9 text-sm" placeholder="Search patient..."
                     value={q} onChange={(e) => setQ(e.target.value)} data-testid="daily-search-input" />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <Button size="sm" variant="outline" className="h-9"
                    onClick={() => setBulkOpen(true)}
                    data-testid="new-bulk-entry-button">
              <Users size={14} className="mr-1.5" /> Add multiple
            </Button>
            <Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white h-9"
                    onClick={() => { setEditEntry(null); setOpen(true); }} data-testid="new-entry-button">
              <Plus size={14} className="mr-1.5" /> New entry
            </Button>
          </div>
        </div>

        {/* Summary card — compact inline row */}
        <Card className="mt-2 border-sky-200 bg-sky-50/60 px-3 py-2 shadow-sm ring-1 ring-sky-100" data-testid="daily-summary-card">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <div className="flex items-baseline gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Summary</span>
              <span className="font-medium text-slate-900" data-testid="daily-summary-date">{fmtDate(summaryDate)}</span>
            </div>
            <span className="hidden sm:inline text-slate-200">|</span>
            <div className="inline-flex items-center gap-1.5">
              <ClipboardList size={12} className="text-slate-500" />
              <span className="text-slate-500">Total</span>
              <span className="font-semibold text-slate-900" data-testid="summary-total">{active.total}</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={12} className="text-emerald-600" />
              <span className="text-emerald-700">Completed</span>
              <span className="font-semibold text-emerald-800" data-testid="summary-completed">{active.completed}</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <Circle size={12} className="text-sky-600" />
              <span className="text-sky-700">Incomplete</span>
              <span className="font-semibold text-sky-800" data-testid="summary-incomplete">{active.incomplete}</span>
            </div>
          </div>
        </Card>
      </div>

      <Card className="mt-3 mb-3 border-slate-200 overflow-hidden [&>div]:max-h-[calc(100vh-142px)] [&>div]:overflow-auto">
        {windowed.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="daily-empty">
            {allRecords
              ? `No entries in the last ${visibleDays} days.${hasMoreOlder ? " Click Load more to see older entries." : ""}`
              : `No entries for ${fmtDate(summaryDate)}.`} Click &quot;New entry&quot; to add one.
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(226,232,240,1)]">
              <TableRow>
                <TableHead className="w-14">S.No.</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {windowed.map((e, i) => {
                const isToday = e.entry_date === todayIso();
                return (
                <TableRow key={e.id}
                          data-testid={`daily-row-${e.id}`}
                          className={isToday ? "bg-sky-50/60 hover:bg-sky-50" : undefined}>
                  <TableCell className="text-slate-500 text-sm w-14" data-testid={`daily-sno-${e.id}`}>{i + 1}</TableCell>
                  <TableCell className="text-slate-700 whitespace-nowrap" data-testid={`daily-date-${e.id}`}>
                    {fmtDate(e.entry_date)}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" onClick={() => setHistoryName(e.patient_name)}
                              data-testid={`daily-name-${e.id}`}
                              className="hover:text-sky-700 hover:underline text-left">
                        {e.patient_name}
                      </button>
                      {e.is_new && (
                        <span
                          data-testid={`daily-new-tag-${e.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700"
                          title="Added without extra details — clears once you open it and take an action"
                        >
                          <Sparkles size={11} /> New
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">by {e.created_by_name}</div>
                  </TableCell>
                  <TableCell>
                    {e.is_new ? (
                      <span className="text-slate-300" data-testid={`daily-status-blank-${e.id}`}>—</span>
                    ) : (
                      <StatusBadge status={e.status} />
                    )}
                  </TableCell>
                  <TableCell className="text-slate-600 max-w-xs truncate">{e.notes || "—"}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {e.is_new ? (
                      <div className="inline-flex items-center gap-2">
                        <Button size="sm"
                                onClick={() => { setEditEntry(e); setOpen(true); }}
                                className="bg-sky-500 hover:bg-sky-600 text-white"
                                data-testid={`complete-entry-${e.id}`}>
                          <Sparkles size={14} className="mr-1" /> Complete Entry
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => deleteEntry(e)}
                                disabled={deletingId === e.id}
                                aria-label={`Delete ${e.patient_name}`}
                                title="Delete this entry"
                                className="h-9 w-9 p-0 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                data-testid={`delete-entry-${e.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button size="sm" variant="outline"
                                onClick={() => { setEditEntry(e); setOpen(true); }}
                                data-testid={`edit-entry-${e.id}`}>
                          Edit
                        </Button>
                        <Link to={`/entries/${e.id}`}>
                          <Button size="sm" variant="outline" data-testid={`open-entry-${e.id}`}>Open</Button>
                        </Link>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {allRecords && windowed.length > 0 && hasMoreOlder && (
          <div className="flex items-center justify-center py-4 border-t border-slate-100 bg-slate-50/60"
               data-testid="daily-load-more-wrap">
            <Button variant="outline" size="sm" onClick={loadMoreDays}
                    data-testid="daily-load-more-button">
              Load 15 more days
            </Button>
          </div>
        )}
      </Card>

      <EntryDialog
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) setEditEntry(null); }}
        entry={editEntry}
        onSaved={load}
      />
      <BulkEntryDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onSaved={load}
      />
      <PatientHistoryDialog
        open={!!historyName}
        onOpenChange={(v) => !v && setHistoryName(null)}
        patientName={historyName}
      />
    </div>
  );
}
